import { useCallback, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ApiUnavailableState, LoadingState, NotConfiguredState } from "../components/states";
import { AdminCard, Pill, RailKv } from "../components/dashboard";
import { SupportLink } from "../components/support-link";
import { DiscordPermissionsPanel } from "../components/discord-permissions";
import { usePlatformStatus } from "../hooks/usePlatformStatus";
import { useServerDirectory } from "../hooks/useServerDirectory";
import { useAppContext } from "../hooks/useAppContext";
import {
  ApiError,
  api,
  type CreatedSnapshot,
  type DisconnectPreview,
  type DisconnectResult,
  type GuildRefreshResult,
  type RefreshedChannel,
  type RemoveFromDiscordResult,
  type RetainedGuildData,
  type ServerDirectoryEntry,
  type ServerDirectorySource,
} from "../lib/api";
import { formatCount, formatDateTime } from "../lib/format";

/**
 * Server — the workspace's Discord servers.
 *
 * This page used to decide what was connected from a guild name kept in
 * `localStorage`. A customer who connected a server on their laptop saw nothing
 * on their phone; a customer with ODIN genuinely installed — the `ProJB's
 * Server` case — saw "connect a server" forever, because no request ever asked
 * the API what existed. The list now comes from
 * `GET /api/v1/tenants/:id/servers`, which reconciles ODIN's records with
 * Discord's view of both the bot and the customer.
 *
 * The page also refuses to round "we could not check" down to "no". When
 * Discord is unreachable the workspace's own servers are still listed, with a
 * line saying what could not be confirmed. A blank page is a worse answer than
 * a partial one, and a *wrong* answer is worse than both.
 *
 * Five things a customer can mean here, kept visibly apart:
 *
 *   1. Install ODIN in Discord      — "Install ODIN in your Discord server"
 *   2. Connect it to this workspace — "Connect to workspace"
 *   3. Repair its permissions       — "Repair ODIN's permissions"
 *   4. Disconnect from workspace    — reversible, keeps history, leaves Discord alone
 *   5. Remove ODIN from Discord     — visible to the whole server, separate button
 *
 * Those five labels are deliberately two matched pairs plus a repair. Install and
 * remove name Discord; connect and disconnect name the workspace. They used to be
 * "Add ODIN to your server", "Link this server" and "Connect this server again"
 * against a "Disconnect from workspace" — three different verbs for two actions,
 * so nothing told a customer which button undid which. A person who has just read
 * "Disconnect from workspace" goes looking for "Connect to workspace", and until
 * now there was no such button anywhere in the product (Part 15).
 *
 * (4) and (5) were previously the same thing, and that thing was clearing
 * `localStorage`: it changed nothing on Discord's side and nothing in the
 * database, so the server reappeared on the next sign-in. They are now two
 * distinct calls with two distinct confirmations, because a customer tidying
 * their dashboard must not accidentally pull a bot out of a live server.
 *
 * A sixth action sits apart from those because it changes nothing at all:
 * "Refresh server" asks Discord what the server looks like now and shows the
 * answer, with the time it was true. Everything else on this page is ODIN's
 * stored record of a server as it was when it was connected, and a customer who
 * has just fixed a permission needs a way to make ODIN look again — and a way
 * to see that it did. It deliberately does not create a snapshot: snapshots are
 * immutable history that deployments and rollback are planned against, and a
 * button that quietly wrote one would let a page refresh rewrite that record.
 */

const STEPS = [
  { title: "Install the ODIN bot", desc: "Invite ODIN to your Discord server with the permissions it needs." },
  { title: "Authorize & select", desc: "Sign in with Discord and pick the server you manage." },
  { title: "Review & apply", desc: "ODIN proposes changes; nothing goes live without your approval." },
] as const;

/** Customer-facing wording for each reconciled state. */
const STATE_LABEL: Record<ServerDirectoryEntry["state"], { tone: "success" | "warning" | "error" | "neutral"; label: string }> = {
  connected: { tone: "success", label: "Connected" },
  awaiting_bot: { tone: "warning", label: "Waiting for ODIN to join" },
  bot_removed: { tone: "error", label: "ODIN was removed" },
  installed_not_connected: { tone: "warning", label: "ODIN is installed — not connected yet" },
  claimed_elsewhere: { tone: "neutral", label: "Connected to another workspace" },
  disconnected: { tone: "neutral", label: "Disconnected by you" },
};

/** What each state means, and what the customer can do about it. */
const STATE_DETAIL: Record<ServerDirectoryEntry["state"], string> = {
  connected: "ODIN is in this server and linked to this workspace.",
  awaiting_bot: "This server is connected, but ODIN has not joined yet. Install ODIN to finish.",
  bot_removed: "This server is connected, but ODIN is no longer in it. Install ODIN again to restore it.",
  installed_not_connected:
    "ODIN is already in this server but it is not connected to this workspace yet. Connect it to manage it here.",
  claimed_elsewhere:
    "Another ODIN workspace already manages this server. Disconnect it there first, or ask its owner.",
  disconnected:
    "You disconnected this server, so ODIN is not managing it. Your snapshots and settings are kept — connect it to this workspace again to pick up where you left off.",
};

export function Server(): JSX.Element {
  const status = usePlatformStatus();
  const { tenantId, tenantName } = useAppContext();
  const [params] = useSearchParams();
  const { state: directory, refresh } = useServerDirectory(tenantId);
  const ready = status.kind === "ready" ? status.data : null;
  const connectResult = params.get("connect");
  // Lifted out of the card on purpose: deleting a server's history removes the
  // card, and a confirmation that disappears with the thing it is confirming
  // leaves the customer unsure whether anything happened.
  const [notice, setNotice] = useState<string | null>(null);

  const servers = directory.kind === "ready" ? directory.data.servers : [];
  const sources = directory.kind === "ready" ? directory.data.sources : [];
  const hasServers = servers.length > 0;

  /**
   * Send the customer to Discord's "Add to Server" for ODIN.
   *
   * With a guild id, Discord pre-selects that server and locks its picker; the
   * cards pass one, because every card names the server its button is about.
   * Without one, Discord shows the full picker, which is correct for the page
   * header's "Connect a server" — that customer has not chosen yet, and the
   * picker is the choosing.
   */
  const startConnect = (discordGuildId?: string): void => {
    if (tenantId) window.location.href = api.sharedConnectUrl(tenantId, discordGuildId);
  };

  const finish = useCallback(
    (message: string): void => {
      setNotice(message);
      refresh();
    },
    [refresh],
  );

  return (
    <div className="ax-content" style={{ gridTemplateColumns: "1fr" }}>
      <div className="ax-workspace">
        <div className="ax-head">
          <div>
            <h1 className="ax-head__title">Servers</h1>
            <p className="ax-head__sub">
              {tenantName ? `Discord servers in ${tenantName}.` : "Your connected Discord servers."}
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button type="button" className="ax-btn ax-btn--secondary" onClick={refresh} disabled={!tenantId}>
              Refresh
            </button>
            {/* Wrapped, not passed: React hands an onClick the mouse event, and
                `startConnect` would read it as a guild id. */}
            <button type="button" className="ax-btn ax-btn--primary" onClick={() => startConnect()} disabled={!tenantId}>
              Connect a server
            </button>
          </div>
        </div>

        {connectResult ? <ConnectBanner result={connectResult} /> : null}

        {notice ? (
          <div style={{ margin: "0.5rem 0 1rem" }} role="status">
            <Pill tone="success" label={notice} />
          </div>
        ) : null}

        {status.kind === "not-configured" ? (
          <NotConfiguredState title="Control API URL not configured">
            Set <code>PUBLIC_API_URL</code> when building the dashboard to see live status here.
          </NotConfiguredState>
        ) : null}
        {/*
          One outage, one message. Both the platform-status probe and the server
          list hit the same API, so when it is down they both fail — and the page
          used to stack two identical "Can't reach the ODIN control API" alerts.
          The server list is what this page is *for*, so its error wins.
        */}
        {status.kind === "unavailable" && directory.kind !== "unavailable" ? (
          <ApiUnavailableState detail={status.detail} />
        ) : null}

        {!tenantId ? (
          <p className="ax-head__sub">First choose or create a workspace using the menu at the top left.</p>
        ) : null}

        {tenantId && directory.kind === "loading" ? <LoadingState label="Checking your servers…" /> : null}
        {tenantId && directory.kind === "unavailable" ? <ApiUnavailableState detail={directory.detail} /> : null}

        <SourceNotices sources={sources} />

        {tenantId && directory.kind === "ready" && hasServers ? (
          <div className="ax-sections">
            {servers.map((server) => (
              <ServerCard
                key={server.discordGuildId}
                server={server}
                tenantId={tenantId}
                onConnect={startConnect}
                onDone={finish}
              />
            ))}
          </div>
        ) : null}

        {tenantId && directory.kind === "ready" && !hasServers ? (
          <section className="ax-connect">
            <h2 className="ax-connect__title">Connect your Discord server</h2>
            <p className="ax-connect__sub">
              Add the ODIN bot to your server in one step — no developer setup, no bot of your own to
              create. ODIN then manages your server through a review-and-apply pipeline and never
              changes Discord without your approval.
            </p>
            <div className="ax-steps">
              {STEPS.map((s, i) => (
                <div className="ax-step" key={s.title}>
                  <span className="ax-step__n">{i + 1}</span>
                  <span className="ax-step__title">{s.title}</span>
                  <span className="ax-step__desc">{s.desc}</span>
                </div>
              ))}
            </div>
            {/*
              The permission panel sits ABOVE the install button, not behind a
              link and not on a settings page. Discord's own authorization screen
              lists permission names with no explanation and every box already
              ticked; a customer who has read nothing beforehand either accepts
              blind or unticks something ODIN needs and then finds a bot that
              cannot do its job. This is the informed half of informed consent,
              and it has to come before the click that leaves the site.
            */}
            <DiscordPermissionsPanel tenantId={tenantId} />
            <div>
              <button type="button" className="ax-btn ax-btn--primary" onClick={() => startConnect()} disabled={!tenantId}>
                Install ODIN in your Discord server
              </button>
            </div>
          </section>
        ) : null}

        {/*
          Also shown once servers exist, because "what can this bot do in our
          server?" is a question that gets asked long after the install — usually
          by someone who was not there for it.
        */}
        {tenantId && directory.kind === "ready" && hasServers ? (
          <DiscordPermissionsPanel tenantId={tenantId} />
        ) : null}

        {ready ? (
          <div className="ax-sections">
            <AdminCard title="Platform & safety">
              <div className="ax-kv-list">
                <RailKv k="Environment" v={ready.environment} />
                <RailKv
                  k="Live deployment"
                  v={<Pill tone={ready.liveDeploymentLocked ? "success" : "warning"} label={ready.liveDeploymentLocked ? "Locked (safe)" : "Enabled"} />}
                />
                <RailKv
                  k="Dry run"
                  v={<Pill tone={ready.safety.dryRun ? "success" : "warning"} label={ready.safety.dryRun ? "On" : "Off"} />}
                />
                <RailKv
                  k="Require approval"
                  v={<Pill tone={ready.safety.requireApproval ? "success" : "error"} label={ready.safety.requireApproval ? "On" : "Off"} />}
                />
              </div>
            </AdminCard>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Which confirmation, if any, the customer is currently being shown. */
type Panel = "none" | "disconnect" | "remove";

type PreviewState =
  | { kind: "loading" }
  | { kind: "ready"; data: DisconnectPreview }
  | { kind: "error"; detail: string };

/**
 * Where a refresh has got to.
 *
 * `failed` and a `done` result carrying `ok: false` are deliberately different
 * states. The first is ODIN's own API refusing or being unreachable — there is
 * no answer at all. The second is ODIN answering that *Discord* did not, which
 * comes with the previous data and a sentence about what to do. Collapsing them
 * would either lose the explanation or blank a card over someone else's outage.
 */
type RefreshState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "done"; data: GuildRefreshResult }
  | { kind: "failed"; detail: string };

/**
 * Where a snapshot has got to. Deliberately the same shape as `RefreshState`,
 * and deliberately a separate value: the two actions are different promises,
 * and one must never be able to overwrite the other's read-out. A refresh that
 * fails should not erase the snapshot you just took, and a snapshot should not
 * clear the live read you were looking at when you took it.
 *
 * `done` covers a stored row whether or not the collection succeeded — a
 * FAILED snapshot is still a real row with a real reason on it, which is what
 * makes it worth showing rather than throwing away.
 */
type SnapshotState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "done"; data: CreatedSnapshot }
  | { kind: "failed"; detail: string };

/**
 * The direct "connect the server ODIN is already in" call.
 *
 * No `done` case on purpose: success refreshes the directory, so the card
 * re-renders from the server's own answer rather than from a local claim that
 * it worked. The only thing worth holding here is a failure.
 */
type ConnectState = { kind: "idle" } | { kind: "running" } | { kind: "failed"; detail: string };

/** "We have not asked" — never rendered as a zero or as a recent time. */
const DASH = <span style={{ color: "var(--text-tertiary)" }}>—</span>;

function ServerCard({
  server,
  tenantId,
  onConnect,
  onDone,
}: {
  server: ServerDirectoryEntry;
  tenantId: string;
  onConnect: (discordGuildId?: string) => void;
  onDone: (message: string) => void;
}): JSX.Element {
  const state = STATE_LABEL[server.state];

  const [panel, setPanel] = useState<Panel>("none");
  const [preview, setPreview] = useState<PreviewState>({ kind: "loading" });
  const [deleteData, setDeleteData] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshState, setRefreshState] = useState<RefreshState>({ kind: "idle" });
  const [snapshotState, setSnapshotState] = useState<SnapshotState>({ kind: "idle" });
  const [connectState, setConnectState] = useState<ConnectState>({ kind: "idle" });

  // What ODIN was told the last time it asked, when it has asked at all. A
  // failed refresh still lands here: it carries the *previous* values on
  // purpose, so reading through it can never show something newer than the
  // truth. `fresh` is the narrower "Discord actually answered" case.
  const live = refreshState.kind === "done" ? refreshState.data : null;
  const fresh = live !== null && live.ok ? live : null;

  const name = fresh?.name ?? server.name;
  const memberCount = fresh ? fresh.memberCount : server.memberCount;
  // A member count of `null` means Discord did not tell us. Rendering that as 0
  // would be a claim we cannot support, about a number the customer can check.
  const members = memberCount === null ? DASH : formatCount(memberCount);
  // A failure only produces health when the failure itself is evidence about
  // health — "the bot is not in this server" is; "rate limited" is not, and
  // arrives as `null` so the stored verdict stands rather than being overwritten
  // with a guess.
  const health = live?.health ?? server.health;
  const blockers = health.findings.filter((f) => f.severity !== "ok");
  const lastRefreshedAt = live?.lastRefreshedAt ?? server.lastRefreshedAt;
  // Three sources can each be the newest, and none of them is reliably newer
  // than the others: the directory row is what was true when the page loaded,
  // a refresh re-reads the stored value, and a snapshot taken on this page is
  // newer than both — until a refresh that started *before* it answers after
  // it. A fixed precedence would let a stale refresh push "Last snapshot"
  // backwards, telling the customer their snapshot did not happen. Taking the
  // maximum cannot do that.
  const takenSnapshot = snapshotState.kind === "done" ? snapshotState.data : null;
  const lastSnapshotAt = latest(
    server.lastSnapshotAt,
    live?.lastSnapshotAt ?? null,
    takenSnapshot?.status === "COMPLETED" ? takenSnapshot.completedAt : null,
  );

  const guildId = server.guildId;
  // Only a server this workspace actually holds a record for can be
  // disconnected or left. A guild another workspace owns, or one ODIN has never
  // been linked to, has no row to act on — and offering the button anyway would
  // promise something the API is right to refuse.
  const holdsRow = guildId !== null && server.state !== "claimed_elsewhere" && server.state !== "installed_not_connected";
  const canDisconnect = holdsRow && server.state !== "disconnected";
  /**
   * Only offered where ODIN has actually seen its bot in the server.
   *
   * `bot_removed` is the obvious exclusion: Discord has confirmed the bot is
   * gone. `awaiting_bot` is the one that produced a bug report. That state means
   * the row exists and ODIN has *never* observed its bot there — either Discord
   * said it is absent, or Discord did not answer — so pressing "Remove ODIN from
   * Discord" sent a leave request for a server the bot is not in. Discord
   * answered 404, ODIN read that honestly as `already_absent`, the notice said
   * nothing changed, and the card did not move. The customer read that as the
   * button being broken, and from where they stood it was: it was offered for a
   * job that did not exist.
   *
   * `disconnected` stays in. Disconnecting is an ODIN-side act that deliberately
   * leaves the bot sitting in Discord, so "I disconnected it and ODIN is still
   * in my server" is a real case with a real button.
   */
  const canOfferRemoval =
    holdsRow && server.state !== "bot_removed" && server.state !== "awaiting_bot";
  // A server the customer has disconnected is one ODIN has been told to leave
  // alone. Spending a Discord call on it — and showing its live channel list —
  // would be doing exactly the thing they asked ODIN to stop doing.
  const canRefresh = holdsRow && server.state !== "disconnected";
  /**
   * Only a live connection can be snapshotted, and the gate stops there.
   *
   * It deliberately does not consult `server.canManage`: that field is the
   * signed-in user's *Discord* MANAGE_GUILD permission, not their ODIN
   * workspace role, and it is false whenever the Discord account view is
   * unavailable. Gating on it would hide the button from customers who are
   * entitled to press it, for reasons that have nothing to do with ODIN. The
   * API checks the workspace role itself and refuses with its own sentence,
   * which is the same division of labour the Refresh button already uses.
   */
  const canSnapshot = holdsRow && server.state === "connected";
  const needsPermissionRepair = health.findings.some(
    (f) => f.code === "missing_permissions" && f.severity !== "ok",
  );

  /**
   * One request per press, and no second press while one is in the air.
   *
   * The button disables itself for the duration, which is what stops the
   * ordinary double-click. It is not the only guard — the API coalesces
   * concurrent refreshes of the same server and rate-limits the endpoint — but
   * it is the one that keeps the customer from being told two different things
   * about the same server a second apart.
   */
  const runRefresh = async (): Promise<void> => {
    if (!guildId) return;
    setRefreshState({ kind: "running" });
    try {
      setRefreshState({ kind: "done", data: await api.refreshServer(tenantId, guildId) });
    } catch (e: unknown) {
      setRefreshState({ kind: "failed", detail: refreshErrorText(e) });
    }
  };

  /**
   * Take a snapshot — a different promise from a refresh, and kept visibly
   * apart from one.
   *
   * A refresh answers "what does Discord say right now" and stores nothing. A
   * snapshot writes a permanent, hashed record of the server's structure that
   * later work — dry-runs, diffs, restores — is measured against. Merging the
   * two would mean either writing history every time someone glanced at a
   * server, or silently never writing it at all.
   *
   * No bot instance is named. The reading is done by the shared reader either
   * way; the instance id is provenance metadata, and requiring one is what made
   * this action unreachable for every customer who had not created an instance.
   *
   * The button disables itself for the duration. That is the double-click
   * guard the customer can see; the API coalesces concurrent collections of the
   * same server and rate-limits the endpoint behind it.
   */
  const runSnapshot = async (): Promise<void> => {
    if (!guildId) return;
    setSnapshotState({ kind: "running" });
    try {
      setSnapshotState({ kind: "done", data: await api.createSnapshot(tenantId, guildId) });
    } catch (e: unknown) {
      setSnapshotState({ kind: "failed", detail: snapshotErrorText(e) });
    }
  };

  /**
   * Connect a server ODIN's bot is already in, without leaving the dashboard.
   *
   * This replaces the redirect that made the customer file the bug. Their card
   * said "ODIN is installed — not connected yet" and its only button, "Connect
   * to workspace", sent them to Discord's authorization screen with a server
   * picker on it — so a button about *this* server asked them to pick a server,
   * and the sentence they wrote back was that connecting a workspace took them
   * to a login where they chose a server instead. Nothing in that trip could
   * tell ODIN something it did not already know: it had the guild id, and it had
   * just told them the bot was there.
   *
   * The button is still allowed to fail, and one failure is worth its own
   * sentence: `bot_not_in_guild` means the card is stale, and the answer is to
   * install ODIN rather than to press this again.
   */
  const runConnectInstalled = async (): Promise<void> => {
    setConnectState({ kind: "running" });
    try {
      const result = await api.connectInstalledGuild(tenantId, server.discordGuildId);
      setConnectState({ kind: "idle" });
      onDone(connectedMessage(name, result.connectionStatus));
    } catch (e: unknown) {
      setConnectState({ kind: "failed", detail: connectErrorText(e) });
    }
  };

  const openPanel = useCallback(
    (next: Exclude<Panel, "none">): void => {
      setPanel(next);
      setError(null);
      setDeleteData(false);
      setUnderstood(false);
      setPreview({ kind: "loading" });
      if (!guildId) return;
      api
        .disconnectPreview(tenantId, guildId)
        .then((data) => setPreview({ kind: "ready", data }))
        .catch((e: unknown) => setPreview({ kind: "error", detail: errorText(e) }));
    },
    [tenantId, guildId],
  );

  const close = useCallback((): void => {
    setPanel("none");
    setError(null);
  }, []);

  const confirmDisconnect = async (): Promise<void> => {
    if (!guildId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.disconnectServer(tenantId, guildId, { deleteData });
      setPanel("none");
      onDone(disconnectMessage(name, result));
    } catch (e: unknown) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmRemoval = async (): Promise<void> => {
    if (!guildId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.removeBotFromDiscord(tenantId, guildId);
      setPanel("none");
      onDone(removalMessage(name, result));
    } catch (e: unknown) {
      // Never smoothed over: the customer is about to open Discord and check.
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminCard title={name}>
      <div style={{ marginBottom: "0.75rem" }}>
        <Pill tone={state.tone} label={state.label} />
      </div>
      <p className="ax-head__sub" style={{ marginTop: 0 }}>
        {STATE_DETAIL[server.state]}
      </p>
      <div className="ax-kv-list">
        <RailKv k="Server ID" v={server.discordGuildId} />
        <RailKv k="Members" v={members} />
        {holdsRow ? (
          <>
            {/*
              Two timestamps, side by side, because they answer two different
              questions and were previously both absent. "Last refreshed" says
              how old the numbers above are. "Last snapshot" says when history
              was last written — refreshing never touches it, and putting them
              together is what stops one being read as the other.
            */}
            <RailKv k="Last refreshed" v={<Timestamp iso={lastRefreshedAt} />} />
            <RailKv k="Last snapshot" v={<Timestamp iso={lastSnapshotAt} />} />
          </>
        ) : null}
      </div>

      {blockers.length > 0 ? (
        <>
          <ul style={{ listStyle: "none", padding: 0, margin: "0.75rem 0 0" }}>
            {blockers.map((finding) => (
              <li key={finding.code} style={{ marginBottom: "0.5rem" }}>
                <Pill tone={finding.severity === "blocked" ? "error" : "warning"} label={finding.summary} />
                <p className="ax-metric__foot" style={{ margin: "0.25rem 0 0" }}>
                  {finding.detail}
                </p>
              </li>
            ))}
          </ul>
          {/*
            A way out, offered at the point of failure (Part 15).

            The buttons below fix the problems ODIN knows how to fix by itself.
            When one of them does not — a permission Discord will not grant, a
            server that keeps coming back unhealthy — the customer was left on
            this card with a red pill and nowhere to go, because the whole
            product's only route to a human was a nav item three screens away
            and one line buried in an error state. The topic is pre-filled so
            nobody has to translate "the bot cannot read this channel" into a
            support category.
          */}
          <p className="ax-metric__foot" style={{ margin: "0.5rem 0 0" }}>
            Still stuck after trying these?{" "}
            <SupportLink topic="BOT_PERMISSIONS">Technical support</SupportLink> — tell us what you
            see and a person will look at this server with you.
          </p>
        </>
      ) : null}

      <div className="ax-rail-actions" style={{ marginTop: "0.75rem", flexWrap: "wrap" }}>
        {canRefresh ? (
          <button
            type="button"
            className="ax-btn ax-btn--secondary"
            onClick={() => void runRefresh()}
            disabled={refreshState.kind === "running"}
          >
            {refreshState.kind === "running" ? "Refreshing…" : "Refresh server"}
          </button>
        ) : null}
        {canSnapshot ? (
          <button
            type="button"
            className="ax-btn ax-btn--secondary"
            onClick={() => void runSnapshot()}
            disabled={snapshotState.kind === "running"}
          >
            {snapshotState.kind === "running" ? "Taking snapshot…" : "Create snapshot"}
          </button>
        ) : null}
        {server.canConnect ? (
          <button
            type="button"
            className="ax-btn ax-btn--primary"
            onClick={() => void runConnectInstalled()}
            disabled={connectState.kind === "running"}
          >
            {connectState.kind === "running" ? "Connecting…" : "Connect to workspace"}
          </button>
        ) : null}
        {/*
          The button this state had no way to offer.

          `awaiting_bot` means the workspace holds a claim on the server and ODIN
          is not in it — which is exactly what the card's own text said, ending
          "Install ODIN to finish." There was nothing to press. The card offered
          Refresh, Disconnect and Remove: one that re-reads a server nothing is
          waiting on, one that gives up the claim, and one that removes a bot
          that is not there. The instruction and the controls disagreed, and the
          controls were what the customer had to live with.
        */}
        {server.state === "awaiting_bot" ? (
          <button
            type="button"
            className="ax-btn ax-btn--primary"
            onClick={() => onConnect(server.discordGuildId)}
          >
            Install ODIN
          </button>
        ) : null}
        {server.state === "bot_removed" ? (
          <button
            type="button"
            className="ax-btn ax-btn--primary"
            onClick={() => onConnect(server.discordGuildId)}
          >
            Install ODIN again
          </button>
        ) : null}
        {server.state === "disconnected" ? (
          <button
            type="button"
            className="ax-btn ax-btn--primary"
            onClick={() => onConnect(server.discordGuildId)}
          >
            Connect to workspace
          </button>
        ) : null}
        {needsPermissionRepair ? (
          <button
            type="button"
            className="ax-btn ax-btn--secondary"
            onClick={() => onConnect(server.discordGuildId)}
          >
            Repair ODIN's permissions
          </button>
        ) : null}
        {canDisconnect ? (
          <button type="button" className="ax-btn ax-btn--secondary" onClick={() => openPanel("disconnect")}>
            Disconnect from workspace
          </button>
        ) : null}
        {canOfferRemoval ? (
          <button type="button" className="ax-btn ax-btn--danger" onClick={() => openPanel("remove")}>
            Remove ODIN from Discord
          </button>
        ) : null}
      </div>

      {connectState.kind === "failed" ? (
        <p className="ax-confirm__error" role="alert" style={{ marginTop: "0.75rem" }}>
          {connectState.detail}
        </p>
      ) : null}

      <RefreshResult state={refreshState} />
      <SnapshotResult state={snapshotState} />

      {panel === "disconnect" ? (
        <DisconnectPanel
          name={name}
          preview={preview}
          deleteData={deleteData}
          onDeleteDataChange={setDeleteData}
          busy={busy}
          error={error}
          onConfirm={confirmDisconnect}
          onCancel={close}
        />
      ) : null}

      {panel === "remove" ? (
        <RemovePanel
          name={name}
          preview={preview}
          understood={understood}
          onUnderstoodChange={setUnderstood}
          busy={busy}
          error={error}
          onConfirm={confirmRemoval}
          onCancel={close}
        />
      ) : null}

      {/*
        "Snapshot history", not "Manage server" (Part 15).
        The destination has always been /app/snapshot, a page that does exactly
        two things: create a snapshot and list the ones already taken. "Manage
        server" promised general server management and delivered neither, and it
        meant the only route to a customer's snapshot history was to take a NEW
        snapshot first and follow the link in the result — asking someone to
        write to their own history in order to read it. The link now says where
        it goes.
      */}
      {server.canManage && server.state === "connected" ? (
        <Link to="/app/snapshot" className="ax-card__link">
          Snapshot history
        </Link>
      ) : null}
    </AdminCard>
  );
}

/**
 * A timestamp the customer can check, or an honest "—".
 *
 * Deliberately absolute rather than relative. "2 minutes ago" stops being true
 * while it is on screen and nothing re-renders it, and — worse — a `null` that
 * gets rounded into "just now" turns "ODIN has never looked at this server"
 * into "ODIN looked a moment ago", which is the exact belief that made a
 * month-old member count look current.
 */
function Timestamp({ iso }: { iso: string | null }): JSX.Element {
  if (iso === null) return DASH;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return DASH;
  return <time dateTime={iso}>{formatDateTime(at)}</time>;
}

/**
 * What Discord said, at the moment it was asked.
 *
 * A read-out, not an editor. The counts and the channel list exist so a
 * customer can answer "is ODIN looking at my server as it is now?" without
 * putting the dashboard and Discord side by side — which is the only way that
 * question could be answered before. None of it is stored: pressing refresh
 * again replaces it, and leaving the page discards it.
 */
function RefreshResult({ state }: { state: RefreshState }): JSX.Element | null {
  if (state.kind === "idle") return null;
  if (state.kind === "running") return <LoadingState label="Asking Discord about this server…" />;

  if (state.kind === "failed") {
    return (
      <p className="ax-confirm__error" role="alert" style={{ marginTop: "0.75rem" }}>
        {state.detail}
      </p>
    );
  }

  const data = state.data;

  // ODIN answered; Discord did not. The card above still shows the previous
  // values — unchanged, not blanked — so this says why they did not move rather
  // than replacing them with an empty server.
  if (!data.ok) {
    const failure = data.error;
    return (
      <div style={{ marginTop: "0.75rem" }} role="alert">
        <Pill
          tone="warning"
          label={failure?.summary ?? "ODIN could not read this server from Discord."}
        />
        {failure ? (
          <p className="ax-metric__foot" style={{ margin: "0.25rem 0 0" }}>
            {failure.detail}
          </p>
        ) : null}
        {failure && failure.retryAfterSeconds !== null ? (
          <p className="ax-metric__foot" style={{ margin: "0.25rem 0 0" }}>
            Discord asked ODIN to wait {failure.retryAfterSeconds} seconds before trying again.
          </p>
        ) : null}
        <p className="ax-metric__foot" style={{ margin: "0.25rem 0 0" }}>
          Nothing was changed. What you see above is still what ODIN had before.
        </p>
      </div>
    );
  }

  const hidden = data.channels.filter((channel) => !channel.visibleToOdin);
  // @everyone is excluded on purpose: ODIN sits above it on every server, so it
  // is never in this list, and mentioning it would only invite the question.
  const outOfReach = data.roles.filter((role) => !role.odinCanManage && !role.isEveryone);
  const groups = groupChannels(data.categories, data.channels);

  return (
    <section style={{ marginTop: "0.75rem" }} aria-label={`What Discord says about ${data.name}`}>
      <div className="ax-kv-list">
        <RailKv k="Roles" v={formatCount(data.counts.roles)} />
        <RailKv k="Categories" v={formatCount(data.counts.categories)} />
        <RailKv k="Channels" v={formatCount(data.counts.channels)} />
        <RailKv k="Permission overrides" v={formatCount(data.counts.overwrites)} />
      </div>

      {hidden.length > 0 ? (
        <p className="ax-metric__foot" style={{ margin: "0.5rem 0 0" }}>
          ODIN cannot see {countOf(hidden.length, "channel")}: {listNames(hidden)}. It will not read
          or manage {hidden.length === 1 ? "it" : "them"} until it is allowed to view{" "}
          {hidden.length === 1 ? "that channel" : "those channels"} in the channel&apos;s own
          permissions.
        </p>
      ) : null}

      {outOfReach.length > 0 ? (
        <p className="ax-metric__foot" style={{ margin: "0.5rem 0 0" }}>
          ODIN cannot assign or edit {countOf(outOfReach.length, "role")} sitting above its own:{" "}
          {listNames(outOfReach)}. That is normal for owner and admin roles — move ODIN&apos;s role
          higher in Server Settings → Roles only if you want it to manage{" "}
          {outOfReach.length === 1 ? "that one" : "those"}.
        </p>
      ) : null}

      {groups.length > 0 ? (
        <details style={{ marginTop: "0.5rem" }}>
          <summary style={{ cursor: "pointer", color: "var(--text-secondary)" }}>
            What ODIN can see right now
          </summary>
          {groups.map((group) => (
            <div key={group.id} style={{ marginTop: "0.5rem" }}>
              <p className="ax-kv__k" style={{ margin: 0 }}>
                {group.name}
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: "0.25rem 0 0" }}>
                {group.channels.map((channel) => (
                  <li key={channel.id} className="ax-metric__foot" style={{ margin: 0 }}>
                    {channelTitle(channel)}
                    {channel.visibleToOdin ? null : (
                      <span style={{ color: "var(--status-warning)" }}> — hidden from ODIN</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </details>
      ) : null}

      <p className="ax-metric__foot" style={{ margin: "0.5rem 0 0" }}>
        This is a live read of Discord. It does not create a snapshot — your snapshot history is
        unchanged.
      </p>
    </section>
  );
}

/**
 * What was written, and when.
 *
 * The refresh read-out above says what Discord is showing at this instant and
 * stores nothing. This one is the opposite: a permanent row that later dry-runs
 * and diffs are measured against. It shows the completion time and the counts
 * that were recorded, so "did that work?" is answerable without leaving the
 * page and without trusting the button's own animation.
 *
 * A FAILED snapshot is shown, not hidden. It is a real row in the customer's
 * history, and the sentence ODIN recorded on it — "ODIN is not in this Discord
 * server… invite ODIN back and try again" — is the only actionable thing on
 * screen at that moment.
 */
function SnapshotResult({ state }: { state: SnapshotState }): JSX.Element | null {
  if (state.kind === "idle") return null;
  if (state.kind === "running") return <LoadingState label="Recording this server's structure…" />;

  if (state.kind === "failed") {
    return (
      <p className="ax-confirm__error" role="alert" style={{ marginTop: "0.75rem" }}>
        {state.detail}
      </p>
    );
  }

  const snap = state.data;

  // ODIN answered; the collection did not finish. The row exists and carries
  // the reason, which is worth more to the reader than the fact that a request
  // returned 502.
  if (snap.status !== "COMPLETED") {
    return (
      <div style={{ marginTop: "0.75rem" }} role="alert">
        <Pill
          tone="warning"
          label={snap.failureSummary ?? "ODIN could not record this server from Discord."}
        />
        <p className="ax-metric__foot" style={{ margin: "0.25rem 0 0" }}>
          Nothing was added to your snapshot history that ODIN can use. Your earlier snapshots are
          unchanged.
        </p>
      </div>
    );
  }

  return (
    <section style={{ marginTop: "0.75rem" }} aria-label="Snapshot taken">
      <Pill tone="success" label="Snapshot saved" />
      <div className="ax-kv-list" style={{ marginTop: "0.5rem" }}>
        <RailKv k="Taken" v={<Timestamp iso={snap.completedAt} />} />
        <RailKv k="Roles" v={formatCount(snap.roleCount)} />
        <RailKv k="Categories" v={formatCount(snap.categoryCount)} />
        <RailKv k="Channels" v={formatCount(snap.channelCount)} />
        <RailKv k="Permission overrides" v={formatCount(snap.overwriteCount)} />
        {snap.snapshotHash === null ? null : (
          <RailKv k="Snapshot ID" v={snap.snapshotHash.slice(0, 12)} />
        )}
      </div>
      {snap.coalesced === true ? (
        <p className="ax-metric__foot" style={{ margin: "0.5rem 0 0" }}>
          A snapshot of this server was already being taken, so this joined it rather than reading
          Discord a second time. The result is the same one snapshot.
        </p>
      ) : null}
      <p className="ax-metric__foot" style={{ margin: "0.5rem 0 0" }}>
        This is saved. Refreshing this server later re-reads Discord but never overwrites a stored
        snapshot. <Link to="/app/snapshot">See snapshot history</Link>
      </p>
    </section>
  );
}

/**
 * The newest of several timestamps, ignoring the ones that are absent.
 *
 * Used where the same fact arrives from more than one place and none of them
 * can be trusted to be the freshest — see `lastSnapshotAt`. Anything
 * unparseable is dropped rather than compared, so a malformed value from one
 * source cannot beat a good value from another.
 */
function latest(...isos: ReadonlyArray<string | null | undefined>): string | null {
  let best: string | null = null;
  let bestMs = Number.NEGATIVE_INFINITY;
  for (const iso of isos) {
    if (typeof iso !== "string" || iso === "") continue;
    const ms = new Date(iso).getTime();
    if (Number.isNaN(ms) || ms <= bestMs) continue;
    best = iso;
    bestMs = ms;
  }
  return best;
}

/** "1 channel" / "3 channels", so the sentence around it reads as English. */
function countOf(n: number, noun: string): string {
  return n === 1 ? `1 ${noun}` : `${n} ${noun}s`;
}

function listNames(items: ReadonlyArray<{ name: string }>): string {
  return items.map((item) => item.name).join(", ");
}

/**
 * Discord's own vocabulary, so what is on screen can be matched against the
 * server sidebar without translation.
 */
function channelTitle(channel: RefreshedChannel): string {
  switch (channel.type) {
    case "text":
      return `#${channel.name}`;
    case "announcement":
      return `${channel.name} (Announcement)`;
    case "forum":
      return `${channel.name} (Forum)`;
    case "media":
      return `${channel.name} (Media)`;
    case "voice":
      return `${channel.name} (Voice)`;
    case "stage":
      return `${channel.name} (Stage)`;
    case "rules":
      return `${channel.name} (Rules)`;
    case "directory":
      return `${channel.name} (Directory)`;
    case "category":
    case "unknown":
      return channel.name;
    // A channel type Discord adds after this ships. Showing the name with no
    // decoration is the one thing that stays true.
    default:
      return channel.name;
  }
}

/**
 * Channels under their categories, in Discord's order.
 *
 * The last group catches two things that must not silently vanish: channels
 * Discord reports outside every category, and channels whose category was not
 * in the response. Dropping either would show a customer fewer channels than
 * they have and label it the live truth.
 */
function groupChannels(
  categories: readonly RefreshedChannel[],
  channels: readonly RefreshedChannel[],
): ReadonlyArray<{ id: string; name: string; channels: readonly RefreshedChannel[] }> {
  const byPosition = (a: RefreshedChannel, b: RefreshedChannel): number => a.position - b.position;
  const known = new Set(categories.map((category) => category.id));
  const groups = [...categories].sort(byPosition).map((category) => ({
    id: category.id,
    name: category.name,
    channels: channels.filter((channel) => channel.parentId === category.id).sort(byPosition),
  }));
  const loose = channels
    .filter((channel) => channel.parentId === null || !known.has(channel.parentId))
    .sort(byPosition);
  return loose.length > 0 ? [...groups, { id: "", name: "No category", channels: loose }] : groups;
}

/**
 * "Are you sure?" is not a confirmation — the customer is agreeing to an
 * outcome nobody described. This panel names the outcome: what stops, what does
 * not change, what ODIN keeps, and whether the bot is still in Discord (with
 * "we could not check" allowed to be the answer).
 */
function DisconnectPanel({
  name,
  preview,
  deleteData,
  onDeleteDataChange,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  name: string;
  preview: PreviewState;
  deleteData: boolean;
  onDeleteDataChange: (next: boolean) => void;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  return (
    <section className="ax-confirm" aria-label={`Disconnect ${name} from this workspace`}>
      <h3 className="ax-confirm__title">Disconnect {name} from this workspace?</h3>

      {preview.kind === "loading" ? <LoadingState label="Checking what this would affect…" /> : null}
      {preview.kind === "error" ? (
        <p className="ax-confirm__error" role="alert">
          {preview.detail}
        </p>
      ) : null}

      {preview.kind === "ready" ? (
        <>
          <ul className="ax-confirm__list">
            {preview.data.consequences.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <p className="ax-metric__foot">{botPresenceLine(preview.data.botStillInDiscord)}</p>

          <h4 className="ax-confirm__subtitle">What ODIN keeps</h4>
          <RetainedSummary retained={preview.data.retained} />

          {preview.data.canPurgeData ? (
            <label className="ax-confirm__check">
              <input
                type="checkbox"
                checked={deleteData}
                onChange={(e) => onDeleteDataChange(e.target.checked)}
              />
              Also permanently delete everything ODIN has stored for this server
            </label>
          ) : (
            <p className="ax-metric__foot">
              Only the workspace owner can delete a server&apos;s ODIN history.
            </p>
          )}

          {deleteData ? (
            <p className="ax-confirm__error" role="alert">
              This cannot be undone. Snapshots, deployments, backups, moderation cases and tickets
              for {name} will be deleted permanently, and connecting the server again will not bring
              them back.
            </p>
          ) : null}
        </>
      ) : null}

      {error ? (
        <p className="ax-confirm__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="ax-rail-actions" style={{ marginTop: "0.75rem" }}>
        <button
          type="button"
          className="ax-btn ax-btn--danger"
          onClick={onConfirm}
          disabled={busy || preview.kind !== "ready"}
        >
          {deleteData ? "Yes, disconnect and delete the history" : "Yes, disconnect this server"}
        </button>
        <button type="button" className="ax-btn ax-btn--secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </section>
  );
}

/**
 * Removal is the one action every member of the Discord server can see, so it
 * gets its own confirmation, its own checkbox, and a manual fallback for when
 * ODIN cannot do it itself. It deliberately does not disconnect the workspace:
 * a customer re-inviting the bot with different permissions should not lose
 * their configuration on the way.
 */
function RemovePanel({
  name,
  preview,
  understood,
  onUnderstoodChange,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  name: string;
  preview: PreviewState;
  understood: boolean;
  onUnderstoodChange: (next: boolean) => void;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  const allowed = preview.kind === "ready" ? preview.data.canRemoveFromDiscord : false;

  return (
    <section className="ax-confirm" aria-label={`Remove ODIN from ${name}`}>
      <h3 className="ax-confirm__title">Remove ODIN from {name} in Discord?</h3>
      <ul className="ax-confirm__list">
        <li>ODIN leaves the Discord server. Everyone in the server can see that it has gone.</li>
        <li>
          Server management stops immediately: no snapshots, no deployments, no moderation actions
          for this server.
        </li>
        <li>
          This server stays connected to this workspace, and ODIN keeps what it has already stored.
          Disconnecting and deleting that history are separate choices.
        </li>
        <li>You can invite ODIN back at any time.</li>
      </ul>
      <p className="ax-metric__foot">
        You can also do this yourself in Discord: Server Settings → Integrations → ODIN → Remove.
      </p>

      {preview.kind === "loading" ? <LoadingState label="Checking this server…" /> : null}
      {preview.kind === "error" ? (
        <p className="ax-confirm__error" role="alert">
          {preview.detail}
        </p>
      ) : null}
      {preview.kind === "ready" && !allowed ? (
        <p className="ax-confirm__error" role="alert">
          ODIN cannot remove itself from this server. Ask a workspace owner or admin, or remove ODIN
          in Discord under Server Settings → Integrations.
        </p>
      ) : null}

      {allowed ? (
        <label className="ax-confirm__check">
          <input
            type="checkbox"
            checked={understood}
            onChange={(e) => onUnderstoodChange(e.target.checked)}
          />
          I understand ODIN will leave {name} and everyone in the server will see it
        </label>
      ) : null}

      {error ? (
        <p className="ax-confirm__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="ax-rail-actions" style={{ marginTop: "0.75rem" }}>
        <button
          type="button"
          className="ax-btn ax-btn--danger"
          onClick={onConfirm}
          disabled={busy || !allowed || !understood}
        >
          Yes, remove ODIN from Discord
        </button>
        <button type="button" className="ax-btn ax-btn--secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </section>
  );
}

const RETAINED_LABELS: ReadonlyArray<readonly [keyof RetainedGuildData, string]> = [
  ["snapshots", "Snapshots"],
  ["deployments", "Deployments"],
  ["backups", "Backups"],
  ["moderationCases", "Moderation cases"],
  ["tickets", "Tickets"],
];

/** Counts, not adjectives: "some history" is not something anyone can decide on. */
function RetainedSummary({ retained }: { retained: RetainedGuildData }): JSX.Element {
  const kept = RETAINED_LABELS.filter(([key]) => retained[key] > 0);
  if (kept.length === 0) {
    return <p className="ax-metric__foot">ODIN has nothing stored for this server yet.</p>;
  }
  return (
    <div className="ax-kv-list">
      {kept.map(([key, label]) => (
        <RailKv key={key} k={label} v={formatCount(retained[key])} />
      ))}
    </div>
  );
}

/** `null` is a third answer here, and it is not "no". */
function botPresenceLine(present: boolean | null): string {
  if (present === true) {
    return "ODIN's bot stays in your Discord server. Removing it from Discord is a separate action.";
  }
  if (present === false) {
    return "ODIN's bot is not currently in this Discord server.";
  }
  return "ODIN could not check whether its bot is still in this Discord server. Either way, disconnecting does not remove it.";
}

function disconnectMessage(name: string, result: DisconnectResult): string {
  const bot =
    result.botStillInDiscord === true
      ? "Its bot is still in your Discord server — removing it is a separate step."
      : result.botStillInDiscord === false
        ? "Its bot is not in the Discord server."
        : "ODIN could not confirm whether its bot is still in the Discord server; it was not removed.";
  const data = result.dataDeleted
    ? "Everything ODIN had stored for it has been deleted."
    : "Your snapshots and settings are kept, so connecting it again restores them.";
  return `ODIN no longer manages ${name}. ${bot} ${data}`;
}

function removalMessage(name: string, result: RemoveFromDiscordResult): string {
  const tail = result.stillConnectedToWorkspace
    ? " It is still connected to this workspace, so you can invite it back without setting it up again."
    : "";
  return result.outcome === "already_absent"
    ? `ODIN was already not in ${name}, so nothing changed in Discord.${tail}`
    : `ODIN has left ${name}.${tail}`;
}

/** The API's sentence when it wrote one; never a stack trace, never silence. */
function errorText(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return "That didn't work. Please try again in a moment.";
}

/**
 * What a direct connect actually achieved, in the customer's terms.
 *
 * Two outcomes, and they are not the same promise. `CONNECTED` means Discord
 * confirmed ODIN is in the server, so the workspace can manage it now.
 * `AWAITING_BOT` means the claim was written but Discord did not answer the
 * presence question — the connection is real and the card will settle once ODIN
 * can see the server. Reporting the second as the first would be the exact
 * failure this page exists to stop: a success message the customer disproves by
 * opening Discord.
 */
function connectedMessage(name: string, connectionStatus: string): string {
  return connectionStatus === "CONNECTED"
    ? `${name} is connected to this workspace.`
    : `${name} is connected to this workspace. ODIN could not reach Discord to confirm its bot is in the server, so this card will finish updating shortly.`;
}

/**
 * A stale card is not a broken button, and the reader needs to be told which.
 *
 * `bot_not_in_guild` is the one refusal with a next step that is not "try
 * again": the card was drawn from a Discord view that has since changed, ODIN is
 * genuinely not in the server, and no amount of pressing Connect will put it
 * there. Say so, and name the button that will.
 */
function connectErrorText(error: unknown): string {
  if (error instanceof ApiError && error.code === "bot_not_in_guild") {
    return "ODIN is not in that Discord server any more, so there is nothing to connect to. Use Refresh server to update this card, then install ODIN again.";
  }
  return errorText(error);
}

/**
 * A 429 from ODIN's own limiter is a different instruction from a 500.
 *
 * The generic wording — "please try again in a moment" — is actively wrong for
 * a rate limit, because trying again immediately is the one thing that keeps it
 * closed. This says what was hit and why it exists, so the reader waits instead
 * of hammering the button and blaming ODIN for a failure it caused on purpose.
 */
function refreshErrorText(error: unknown): string {
  if (error instanceof ApiError && error.status === 429) {
    return "You have refreshed this server several times in the last few minutes. Wait a moment and try again — ODIN limits this so Discord does not rate-limit the whole platform.";
  }
  return errorText(error);
}

/**
 * The same reasoning as `refreshErrorText`, with the limit that was actually
 * hit named. Snapshots have their own, tighter budget because each press writes
 * a permanent row as well as spending Discord calls, so "you refreshed too
 * often" would send the reader looking at the wrong button.
 *
 * A 403 is worth its own sentence too: the API refuses viewers here, and the
 * default "that didn't work, try again" would have someone retrying a thing
 * that will never succeed for them.
 */
function snapshotErrorText(error: unknown): string {
  if (error instanceof ApiError && error.status === 429) {
    return "You have taken several snapshots of this server in the last few minutes. Wait a moment and try again — ODIN limits this so one busy server cannot slow down the whole platform.";
  }
  if (error instanceof ApiError && error.status === 403) {
    return error.message ||
      "Your role in this workspace cannot take snapshots. Ask an owner or admin to take one.";
  }
  return errorText(error);
}

/**
 * Say plainly which of the three views could not be checked.
 *
 * Silence here is what produced the original bug report: the page looked
 * authoritative while one of its inputs had failed, so "no servers" and "we
 * could not ask Discord" were indistinguishable to the person reading it.
 */
function SourceNotices({ sources }: { sources: ServerDirectorySource[] }): JSX.Element | null {
  const degraded = sources.filter((s) => s.status !== "ok");
  if (degraded.length === 0) return null;

  const text = (source: ServerDirectorySource): string => {
    // Checked first because this one is not about servers at all: it is about
    // ODIN's own Discord application settings, and the generic presence wording
    // below would tell the customer to go look in the wrong place.
    if (source.source === "bot_application") {
      return "ODIN could not read its own Discord application settings, so a missing Server Members Intent would not be reported below.";
    }
    if (source.status === "reauth_required") {
      return "Your Discord sign-in has expired, so servers you have not already linked are not shown. Sign in with Discord again to see them.";
    }
    if (source.status === "not_configured") {
      return "ODIN cannot confirm which servers it is in right now. Anything already linked is still shown.";
    }
    return source.source === "discord_account"
      ? "Discord did not respond, so servers you have not already linked may be missing. Anything already linked is still shown."
      : "Discord did not respond, so ODIN's presence in these servers could not be confirmed.";
  };

  // Named, because it is not the only live region on this page any more and a
  // test that asks "is there a warning here?" must be able to mean this one.
  return (
    <div style={{ margin: "0.5rem 0 1rem" }} role="status" aria-label="Server list warnings">
      {degraded.map((source) => (
        <p className="ax-head__sub" key={`${source.source}:${source.status}`} style={{ margin: "0 0 0.25rem" }}>
          {text(source)}
        </p>
      ))}
    </div>
  );
}

/** Banner shown when Discord redirects back to /app/servers?connect=… */
function ConnectBanner({ result }: { result: string }): JSX.Element | null {
  const map: Record<string, { tone: "success" | "warning" | "error"; text: string }> = {
    success: { tone: "success", text: "ODIN was added to your server. You're connected." },
    error: { tone: "error", text: "That connection didn't complete. Please try adding ODIN again." },
    forbidden: {
      tone: "warning",
      text: "You need to be an owner or admin of the workspace to connect a server.",
    },
    unavailable: {
      tone: "warning",
      text: "Server connection isn't available yet. Please try again shortly.",
    },
    // These three used to all arrive as "error", which told the customer to
    // retry an action that would have failed the same way every time.
    expired: {
      tone: "warning",
      text: "That connect link timed out before Discord sent you back. Please start again — it only takes a moment.",
    },
    claimed: {
      tone: "warning",
      text: "That server is already connected to another ODIN workspace. Disconnect it there first, or ask its owner.",
    },
    reauth: {
      tone: "warning",
      text: "Your Discord sign-in expired during the connection. Sign in with Discord again, then retry.",
    },
  };
  const entry = map[result];
  if (!entry) return null;
  return (
    <div style={{ margin: "0.5rem 0 1rem" }}>
      <Pill tone={entry.tone} label={entry.text} />
    </div>
  );
}
